interface FPSStats {
  current: number;
  average: number;
  min: number;
  max: number;
  samples: number;
}

export const measureFPS = (duration = 5000): Promise<FPSStats> => {
  return new Promise((resolve) => {
    let frameCount = 0;
    const startTime = performance.now();
    const fpsReadings: number[] = [];
    let lastSecond = startTime;
    let lastSecondFrames = 0;

    const measure = () => {
      const now = performance.now();
      frameCount++;
      lastSecondFrames++;

      // Record FPS every second
      if (now - lastSecond >= 1000) {
        const fps = Math.round((lastSecondFrames * 1000) / (now - lastSecond));
        fpsReadings.push(fps);
        lastSecondFrames = 0;
        lastSecond = now;
      }

      // Continue measuring until duration is reached
      if (now - startTime < duration) {
        requestAnimationFrame(measure);
      } else {
        // Calculate final stats
        const totalTime = now - startTime;
        const averageFPS = Math.round((frameCount * 1000) / totalTime);

        resolve({
          current: averageFPS,
          average:
            fpsReadings.length > 0
              ? Math.round(
                  fpsReadings.reduce((sum, fps) => sum + fps, 0) /
                    fpsReadings.length
                )
              : averageFPS,
          min: fpsReadings.length > 0 ? Math.min(...fpsReadings) : averageFPS,
          max: fpsReadings.length > 0 ? Math.max(...fpsReadings) : averageFPS,
          samples: fpsReadings.length,
        });
      }
    };

    requestAnimationFrame(measure);
  });
};

// Measure memory usage over a specified duration
export const measureMemory = (
  duration = 5000
): Promise<{ used: number; total: number }> => {
  return new Promise((resolve) => {
    const memoryReadings: { used: number; total: number }[] = [];
    const startTime = performance.now();

    const measure = () => {
      const memoryInfo =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "memory" in performance ? (performance as any).memory : null;

      if (memoryInfo) {
        memoryReadings.push({
          used: memoryInfo.usedJSHeapSize,
          total: memoryInfo.jsHeapSizeLimit,
        });
      } else {
        // Fallback if memory API is not available
        memoryReadings.push({
          used: 0,
          total: 0,
        });
      }

      // Continue measuring until duration is reached
      if (performance.now() - startTime < duration) {
        requestAnimationFrame(measure);
      } else {
        // Calculate average memory usage
        const totalUsed = memoryReadings.reduce(
          (sum, reading) => sum + reading.used,
          0
        );
        const totalMemory = memoryReadings.reduce(
          (sum, reading) => sum + reading.total,
          0
        );

        resolve({
          used: Math.round(totalUsed / memoryReadings.length),
          total: Math.round(totalMemory / memoryReadings.length),
        });
      }
    };

    requestAnimationFrame(measure);
  });
};
